// GxTabs — جایگزینِ drop-in برای AntD Tabs با قابلیتِ جابه‌جاییِ تب‌ها (کشیدن)
// و ذخیرهٔ ترتیب برای هر کاربر. اگر layoutId داده نشود، شناسهٔ پایدار از روی
// کلیدِ تب‌ها ساخته می‌شود.
//
// با propِ `urlKey` تبِ فعال در آدرسِ صفحه می‌نشیند و «برگشت»ِ مرورگر به تبِ
// قبلی برمی‌گردد، نه به صفحهٔ قبلیِ منو. اگر والد خودش `activeKey` بدهد
// (تبِ کنترل‌شده)، GxTabs دست به آدرس نمی‌زند و مدیریت با والد است.
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Tabs } from "antd";
import { loadOrder, saveOrder, autoId } from "../utils/layoutStore";
import useUrlTab from "../utils/useUrlTab";

export default function GxTabs({
  layoutId, items = [], urlKey, activeKey, defaultActiveKey, onChange, ...rest
}) {
  const keys = useMemo(() => items.map((it) => String(it.key)), [items]);
  const id = useMemo(() => layoutId || autoId(keys), [layoutId, keys]);
  const [order, setOrder] = useState(() => loadOrder("tab", id));
  useEffect(() => { setOrder(loadOrder("tab", id)); }, [id]);

  const ordered = useMemo(() => {
    if (!order || !order.length) return items;
    const byKey = new Map(items.map((it) => [String(it.key), it]));
    const used = new Set();
    const out = [];
    order.forEach((k) => { if (byKey.has(k)) { out.push(byKey.get(k)); used.add(k); } });
    items.forEach((it) => { if (!used.has(String(it.key))) out.push(it); });
    return out;
  }, [items, order]);

  const move = useCallback((from, to) => {
    const cur = ordered.map((it) => String(it.key));
    const fi = cur.indexOf(from), ti = cur.indexOf(to);
    if (fi < 0 || ti < 0) return;
    cur.splice(ti, 0, cur.splice(fi, 1)[0]);
    saveOrder("tab", id, cur);
    setOrder(cur);
  }, [ordered, id]);

  const renderTabBar = useCallback((barProps, DefaultTabBar) => (
    <DefaultTabBar {...barProps}>
      {(node) => (
        <div
          key={node.key}
          draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/gxtab", String(node.key)); }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
          onDrop={(e) => {
            e.preventDefault();
            const from = e.dataTransfer.getData("text/gxtab");
            if (from && from !== String(node.key)) move(from, String(node.key));
          }}
          style={{ cursor: "grab" }}
          title="برای جابه‌جایی، تب را بکشید"
        >
          {node}
        </div>
      )}
    </DefaultTabBar>
  ), [move]);

  /* هوک همیشه صدا زده می‌شود (قاعدهٔ هوک‌ها)؛ فقط وقتی `urlKey` هست به کار
     می‌آید. `keys` فهرستِ مجاز است تا کلیدِ ناشناختهٔ آدرس صفحه را خالی نکند. */
  const [urlTab, setUrlTab] = useUrlTab(urlKey || "tab", defaultActiveKey || keys[0], keys);
  const controlled = activeKey !== undefined;
  const syncUrl = !!urlKey && !controlled;

  const handleChange = useCallback((k) => {
    if (syncUrl) setUrlTab(k);
    onChange?.(k);
  }, [syncUrl, setUrlTab, onChange]);

  const tabProps = { ...rest, items: ordered, renderTabBar, onChange: handleChange };
  if (controlled) tabProps.activeKey = activeKey;
  else if (syncUrl) tabProps.activeKey = urlTab;
  else if (defaultActiveKey !== undefined) tabProps.defaultActiveKey = defaultActiveKey;

  return <Tabs {...tabProps} />;
}
